import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * **A popover's late problem** (spec twin-details-page-hours-special, R7; ruling 3): the one piece of
 * machinery the Opening hours step's popovers share — *New Standard Hours From a Date*, *Remove*,
 * *Copy another twin* and a special period's *Delete*.
 *
 * Each popover sends something whose answer can land after it has closed (Escape, Cancel), or after
 * the read that follows a refusal has taken it away with its owner. A problem must never be written
 * into a closed popover, and never lost: while the popover is open it shows there; once it has closed
 * it goes to the step (`onLateProblem`), which shows and announces it.
 *
 * - `report(problem)` shows `problem` while the popover is open, and hands it to `onLateProblem` when
 *   it has closed. `report(null)` clears what is shown.
 * - On unmount with the popover open and a problem showing, that problem goes to `onLateProblem`,
 *   once.
 * - `{ forward: false }` shows a problem that is the popover's alone — *Copy another twin*'s source
 *   read, about an attempt nothing was written for — never handed to the step. The hook forwards only
 *   what the caller reports for forwarding.
 */
export interface PopoverProblem {
  open: boolean;
  show: () => void;
  close: () => void;
  problem: string | null;
  report: (problem: string | null, options?: { forward?: boolean }) => void;
  /** Open at this moment — for an answer that lands after a render, when `open` may be stale. */
  isOpen: () => boolean;
}

export const usePopoverProblem = (onLateProblem: (sentence: string) => void): PopoverProblem => {
  /** Open now: an answer that lands after Escape must not write into a closed popover. */
  const openRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /** What the popover shows now that the step must keep if the popover goes. */
  const forwardRef = useRef<string | null>(null);
  const lateRef = useRef(onLateProblem);
  lateRef.current = onLateProblem;

  useEffect(
    () => () => {
      if (openRef.current && forwardRef.current) lateRef.current(forwardRef.current);
      openRef.current = false;
    },
    [],
  );

  const showProblem = (next: string | null, forward: boolean) => {
    forwardRef.current = forward ? next : null;
    setProblem(next);
  };

  const show = useCallback(() => {
    openRef.current = true;
    showProblem(null, true);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    // What waits to be forwarded is left: the unmount effect forwards only while open, and `show`
    // clears it.
    openRef.current = false;
    setOpen(false);
    setProblem(null);
  }, []);

  const report = useCallback(
    (next: string | null, { forward = true }: { forward?: boolean } = {}) => {
      if (openRef.current) showProblem(next, forward);
      else if (next !== null && forward) lateRef.current(next);
    },
    [],
  );

  const isOpen = useCallback(() => openRef.current, []);

  return { open, show, close, problem, report, isOpen };
};
